import React, { useState, useRef, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { FaPrint } from "react-icons/fa";
import { IoMdDownload } from "react-icons/io";
import ReactToPrint, { PrintContextConsumer } from "react-to-print";
import "./receipt.css";
import { utils } from "../../../utils/Utils.jsx";
import PrintPDFComponent from "../../printPDF/PrintPDF.jsx";

const ReceiptComponent = (props) => {
  const [loadingDownload, setLoadingDownload] = useState(false);
  const componentRef = useRef();
  const history = useHistory();

  // Download PDF functionality
  const downloadPDF = useCallback(() => {
    if (!props.fileName) return;

    setLoadingDownload(true);
    utils.generatePDFFromReceipt(props.fileName, (state) =>
      setLoadingDownload(state.loadingDownload)
    );
  }, [props.fileName]);

  useEffect(() => {
    console.log("Receipt fix", props);

    // Auto-download PDF if requested and receipt data is available
    if (props.autoDownload && props.data && props.fileName) {
      const timer = setTimeout(() => {
        downloadPDF();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [props.autoDownload, props.data, props.fileName, downloadPDF]);

  // Receipt Actions Component (Download & Print buttons)
  const renderReceiptActions = () => {
    if (!props.showActions) return null;

    return (
      <div className="receipt-actions-container">
        <div className="icons-row">
          {loadingDownload ? (
            <div className="loader-icon">
              <Spinner className="loader-download left-icon" />
            </div>
          ) : (
            <IoMdDownload className="left-icon" onClick={downloadPDF} />
          )}

          <ReactToPrint content={() => componentRef.current}>
            <PrintContextConsumer>
              {({ handlePrint }) => (
                <FaPrint className="right-icon" onClick={handlePrint} />
              )}
            </PrintContextConsumer>
          </ReactToPrint>
        </div>
      </div>
    );
  };

  // Hidden Print Component
  const renderHiddenPrintDiv = () => {
    if (!props.showActions || !props.printData) return null;

    return (
      <div id="hidden-div" style={{ display: "none" }}>
        <div
          className="detail-container-print"
          ref={componentRef}
          id="pdf"
          style={{
            fontFamily: "Arial, sans-serif",
            color: "#000000",
            backgroundColor: "#ffffff",
            padding: "20px",
          }}
        >
          <PrintPDFComponent
            theme={props.theme}
            title={props.printData.title || "Receipt Details"}
            receiptData={props.data}
            text={props.printData.text || "Powered by Blox"}
            localizedText={props.localizedText}
          />
        </div>
      </div>
    );
  };

  // Group data by categories
  const categorizeData = (data) => {
    const categories = {
      metadata: [],
      amounts: [],
      fees: null,
      totals: null,
      destinationAmounts: [],
    };

    data.forEach((item) => {
      switch (item.type) {
        case "normal":
          categories.metadata.push(item);
          break;
        case "amount":
          categories.amounts.push(item);
          break;
        case "destination-amount":
          categories.destinationAmounts.push(item);
          break;
        case "fees":
          categories.fees = item;
          break;
        case "totals":
          categories.totals = item;
          break;
        case "date":
          categories.metadata.push(item);
          break;
        default:
          categories.metadata.push(item);
      }
    });

    return categories;
  };

  const renderMetadataSection = (items) => {
    console.log("Metadata Items", items);
    if (items.length === 0) return null;

    return (
      <div className="receipt-section lighter-bg-from-text-01">
        {items.map((item, index) => (
          <div key={index} className="receipt-row">
            <div className="key">{item.name}</div>
            <div className="value">
              {item.type === "date"
                ? utils.formatDateUSWithTime(item.value)
                : item.value}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAmountsSection = (amounts, fees, totals) => {
    console.log("Amounts", amounts, fees, totals);
    const hasAmountData =
      amounts.length > 0 ||
      (fees && fees.value && fees.value.length > 0) ||
      (totals && totals.value && totals.value.length > 0);
    if (!hasAmountData) return null;

    return (
      <div className="receipt-section lighter-bg-from-text-01">
        {/* Base Amounts */}
        {amounts.map((item, index) => (
          <div
            key={`amount-${index}`}
            className="receipt-row mb-2"
            style={{ fontSize: "0.825rem" }}
          >
            <div className="key">{item.name}</div>
            <div className="value">
              {utils.roundDecimals(item.currency, item.value) +
                " " +
                item.currency}
            </div>
          </div>
        ))}

        {/* Fees Subsection */}
        {fees && fees.value && fees.value.length > 0 && (
          <div
            className="receipt-subsection"
            style={{ fontSize: "0.825rem !important" }}
          >
            <div
              className="receipt-subsection-title"
              style={{ fontSize: "0.8rem" }}
            >
              {fees.name}
            </div>
            {fees.value.map((fee, feeIndex) => (
              <div key={`fee-${feeIndex}`} className="receipt-row fee-row">
                <div className="key">
                  {fee.name} {fee.currency}
                </div>
                <div className="value">
                  {Math.abs(utils.roundDecimals(fee.currency, fee.amount)) +
                    " " +
                    fee.currency}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Totals Subsection */}
        {totals && totals.value && totals.value.length > 0 && (
          <div className="receipt-subsection mt-2">
            <div className="receipt-subsection-title">Total</div>
            {totals.value.map((total, totalIndex) => (
              <div
                key={`total-${totalIndex}`}
                className="receipt-row total-row"
              >
                <div className="key">
                  {totals.name} {total.currency}
                </div>
                <div className="value total-amount">
                  {utils.roundDecimals(total.currency, total.amount) +
                    " " +
                    total.currency}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDestinationAmountsSection = (destinationAmounts) => {
    if (destinationAmounts.length === 0) return null;

    return (
      <div className="receipt-section lighter-bg-from-text-01">
        <div className="receipt-subsection">
          {destinationAmounts.map((item, index) => (
            <div key={`destination-${index}`} className="receipt-row">
              <div className="key">{item.name}</div>
              <div className="value">
                {utils.roundDecimals(item.currency, item.value) +
                  " " +
                  item.currency}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="receipt-with-actions-container">
        {props.data.map((item, itemIndex) => {
          const categories = categorizeData(item.data);

          return (
            <div key={itemIndex} className="receipt-container">
              <div className="receipt-title">{item.title}</div>

              {/* Render sections in logical order */}
              {renderMetadataSection(categories.metadata)}
              {renderAmountsSection(
                categories.amounts,
                categories.fees,
                categories.totals
              )}
              {renderDestinationAmountsSection(categories.destinationAmounts)}
            </div>
          );
        })}

        {/* Render download/print actions if enabled */}
        {renderReceiptActions()}
      </div>

      {/* Hidden print div for PDF generation */}
      {renderHiddenPrintDiv()}
    </>
  );
};

export default ReceiptComponent;
